import { type FC, useState } from 'react';

import {
  Button,
  ButtonGroup,
  Divider,
  HStack,
  IconButton,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { t } from 'i18next';

import {
  AddOrEditCandidateModal,
  useIsAdminTemplateInterview,
  useShareToken,
} from '@/features/admin-interview';
import { ImportExcelModal } from '@/features/candidate/ImportExcelModal';

import {
  IconExcel,
  IconPlus,
  IconRightArrow,
  IconUserPlus,
} from '../../components/Icons';
import { PopoverItemRow } from '../../components/Popover';

export const AddCandidate: FC<{ interviewId: string }> = ({ interviewId }) => {
  const shareToken = useShareToken();
  const [isSingleCandidateAddModalOpen, setIsSingleCandidateAddModalOpen] =
    useState(false);
  const [isAddWithExcelModalOpen, setIsAddWithExcelModalOpen] = useState(false);
  const isAdminTemplateInterview = useIsAdminTemplateInterview();

  const popover = useDisclosure();

  const addCandidateOptions = [
    {
      id: 'addNewCandidate',
      text: t('candidate:inviteNewCandidate.addNewCandidate'),
      icon: <IconUserPlus fontSize="20px" mr={2} />,
      onClick: () => setIsSingleCandidateAddModalOpen(true),
      isDisabled: !!shareToken,
    },
    {
      id: 'addFromExcel',
      text: t('common:actions.addFromExcel'),
      icon: <IconExcel fontSize="20px" mr={2} />,
      onClick: () => setIsAddWithExcelModalOpen(true),
      isDisabled: !!shareToken,
    },
  ] as const;

  return (
    <HStack>
      <Popover
        closeOnEsc
        closeOnBlur
        placement="bottom-start"
        returnFocusOnClose={false}
        isOpen={popover.isOpen}
        onClose={popover.onClose}
      >
        <PopoverAnchor>
          <ButtonGroup
            variant="@primary"
            size="md"
            isAttached
            onClick={popover.onToggle}
            isDisabled={!!shareToken || isAdminTemplateInterview}
          >
            <Button
              _hover={{
                bg: 'brand.700',
              }}
              leftIcon={<IconPlus />}
            >
              {t('candidate:inviteNewCandidate.addNewCandidate')}
            </Button>
            <IconButton
              aria-label="add candidate"
              icon={
                <IconRightArrow
                  transform={`${popover.isOpen ? 'rotate(270deg)' : 'rotate(90deg)'}`}
                  transition="0.3s"
                />
              }
              _hover={{
                bg: 'brand.700',
              }}
            />
          </ButtonGroup>
        </PopoverAnchor>
        <Portal>
          <PopoverContent
            w="fit-content"
            borderRadius="xl"
            borderColor="gray.100"
            shadow="xl"
          >
            <PopoverBody p={0}>
              {addCandidateOptions.map((item, index) => {
                return (
                  <>
                    <PopoverItemRow
                      showArrow={false}
                      key={item.id}
                      name={item.text}
                      icon={item.icon}
                      onRowClick={item.onClick}
                      isDisabled={item.isDisabled}
                    />
                    {index !== addCandidateOptions.length - 1 && <Divider />}
                  </>
                );
              })}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>

      <ImportExcelModal
        interviewId={interviewId}
        isOpen={isAddWithExcelModalOpen}
        onClose={() => setIsAddWithExcelModalOpen(false)}
      />

      <AddOrEditCandidateModal
        isOpen={isSingleCandidateAddModalOpen}
        onClose={() => setIsSingleCandidateAddModalOpen(false)}
        interviewId={interviewId}
        isEdit={false}
      />
    </HStack>
  );
};
